import { set, subDays } from 'date-fns';
import { createSelector } from 'reselect';

import { CHAT } from 'constants/ExtensionConstants';
import { INTERVENTION_MODE } from 'constants/InterventionConstants';
import { REMINDER } from 'constants/ModuleConstants';
import { SELF_SERVICE_STATS_ORIGIN, SELF_SERVICE_TYPE } from 'constants/SelfServiceConstants';
import { REQUEST_DATE_FORMATTER, getStatsModules } from 'constants/StatisticsConstants';

import { getBrandByIds } from 'modules/brands/selectors';
import { isSelfServiceStats } from 'modules/router/selectors';
import { getEffectiveGroupBy } from 'modules/statistics/groupBy';
import {
  getCurrentUser,
  getCurrentUserIsAdmin,
  getCurrentUserBrandIds,
  getCurrentUserIsManufacturer,
} from 'modules/users/selectors/currentUser';
import {
  getSelectedDealer,
  getSelectedDealerId,
  getSelectedDealerGroupId,
  getDealersBySelectedGroupId,
  getSelectedDealerExtensions,
} from 'selectors/dealerSelectors';
import { humanize } from 'utils/global';

/**
 * @param {{ dealerStatistics : import('modules/statistics/reducer').StatisticsReducer}} state
 * @returns {import('modules/statistics/reducer').StatisticsReducer}
 */
const getState = state => state.dealerStatistics;

const getIntersectModules = (mode, modules, isSelfServiceStatsLocation, features = []) => {
  const defaultModules = getStatsModules(mode, isSelfServiceStatsLocation, features);
  return defaultModules.filter(module => modules.length === 0 || modules.includes(module));
};

const getRoot = root => root;
const getProps = (_, props) => props;

const getUI = createSelector(getState, state => state.ui);
const getUICommon = createSelector(getUI, state => state.common);
const getUIDetails = createSelector(getUI, state => state.details);
const getUIExport = createSelector(getUI, state => state.export);
const getUISelfServiceDetails = createSelector(getUI, state => state.selfServiceDetails);

export const getMode = createSelector(getUIDetails, ui => ui.mode);
export const getPeriod = createSelector(getUICommon, ui => ui.period);
export const getDealersStatsFilters = createSelector(getUICommon, ui => ui.filters);

export const getDealersStatsCountryFilters = createSelector(
  getDealersStatsFilters,
  ({ countryCodes }) => countryCodes?.[0]
);

export const getLatestRefreshTime = createSelector(
  getUICommon,
  state => (state.refreshTime ? state.refreshTime : subDays(set(new Date(), { minutes: 0 }), 1).toISOString())
);

export const isInit = createSelector(getUIDetails, details => details?.isInit);

export const getFilteredDateField = createSelector(getDealersStatsFilters, filters => filters.dateField);
export const getFilteredModules = createSelector(getDealersStatsFilters, filters => filters.modules);
export const getBrandIds = createSelector(getDealersStatsFilters, filters => filters.brandIds ?? []);

export const getBrandFilterRequestParams = createSelector(
  getSelectedDealerGroupId,
  getSelectedDealerId,
  getCurrentUserBrandIds,
  (groupId, dealerId, brandIds = []) => {
    const hasBrands = brandIds.length > 0;
    if (hasBrands) {
      return { brandIds };
    }
    // `|| undefined` avoid to send an empty string
    return { dealerId: dealerId || undefined, groupId: groupId || undefined };
  }
);

export const isExporting = createSelector(getUIExport, state => state.isExporting);
export const isExportModalVisible = createSelector(getUIExport, state => state.visible);

const getSelectedBrands = createSelector(getRoot, getBrandIds, getBrandByIds);

export const getModules = createSelector(
  getMode,
  getFilteredModules,
  getSelectedDealer,
  isSelfServiceStats,
  getCurrentUser,
  (mode, modules, dealer, isSelfServiceStatsLocation, { data }) => {
    const { features } = dealer ?? {};
    return getIntersectModules(mode, modules, isSelfServiceStatsLocation, features ?? data.additionalData?.features ?? []).join('~');
  }
);

export const getFilterModulesCollection = createSelector(
  getMode,
  getCurrentUser,
  isSelfServiceStats,
  getCurrentUserIsManufacturer,
  (mode, user, isSelfServiceStatsLocation, isManufacturer) => {
    const { features = [] } = user.data.additionalData ?? {};
    const newFeatures = isManufacturer ? [...features, REMINDER] : features;
    return getStatsModules(mode, isSelfServiceStatsLocation, newFeatures);
  }
);

export const getBaseRequestParams = createSelector(
  getPeriod,
  getSelectedDealerGroupId,
  getSelectedDealerId,
  getDealersStatsFilters,
  getMode,
  getModules,
  getCurrentUserIsAdmin,
  ({ from, to }, groupId, dealerId, { brandIds: brands, countryCodes, dateField }, mode, modules, isAdmin) => {
    const period = REQUEST_DATE_FORMATTER[dateField]?.transform?.(from, to) ??
      REQUEST_DATE_FORMATTER.DEFAULT.transform(from, to);

    return {
      ...period,
      dateField,
      groupId: groupId || undefined,
      dealerId: dealerId || undefined,
      countryCodes,
      brands,
      mode,
      modules: modules.split('~').filter(Boolean),
      demo: isAdmin ? undefined : false,
    };
  }
);

export const getRequestParamsWithVideoFilter = createSelector(
  getBaseRequestParams,
  getDealersStatsFilters,
  (params, { hasVideo }) => ({
    ...params,
    hasVideo: params.mode === INTERVENTION_MODE.TECHNICIAN && hasVideo ? true : undefined,
  })
);

export const getChatRequestParams = createSelector(
  getPeriod,
  getBaseRequestParams,
  ({ from, to }, { sentFrom: _, sentTo: __, startDateFrom: _startDateFrom, startDateTo: _startDateTo, dateField: _dateField, ...baseRequest }) => ({
    from,
    to,
    ...baseRequest,
  })
);

export const getDealersStatsParams = createSelector(
  getSelectedDealerId,
  getSelectedDealerGroupId,
  getRequestParamsWithVideoFilter,
  getDealersStatsFilters,
  getModules,
  getMode,
  (
    dealerId,
    groupId,
    params,
    { page, size, sort, displayAllInterventions: displayAll, countryCodes },
    modules,
    mode
  ) => ({
    ...params,
    groupId,
    dealerId,
    displayAll,
    size,
    page,
    sort,
    countryCodes,
    modules: modules.split('~'),
    mode
  })
);

export const getAllDealersStatsParams = createSelector(
  getMode,
  getSelectedDealerGroupId,
  getRequestParamsWithVideoFilter,
  getDealersStatsFilters,
  getModules,
  (mode, groupId, params, { page, size, sort, displayAllInterventions: displayAll, countryCodes }, modules) => ({
    ...params,
    groupId,
    displayAll,
    size,
    page,
    sort,
    countryCodes,
    modules: modules.split('~'),
    mode,
  })
);

export const getFormattedFilters = createSelector(
  getProps,
  getFilteredModules,
  getSelectedBrands,
  getModules,
  (intl, tmpModules, brands, availableModulesStr) => {
    let filters = [];
    const availableModules = availableModulesStr.split('~');
    const modules = tmpModules.filter(module => availableModules.includes(module));
    const hasModule = modules.length > 0;
    const hasBrand = brands.length > 0;

    if (hasModule) {
      filters = [
        ...filters,
        {
          id: 'modules',
          name: intl.formatMessage({ id: 'page.home.filters.type.modules', defaultMessage: 'Modules' }),
          values: modules.map(module => ({
            value: module,
            name: intl.formatMessage({ id: module.toLowerCase(), defaultMessage: humanize(module) }),
            className: 'default',
          })),
        },
      ];
    }

    if (hasBrand) {
      filters = [
        ...filters,
        {
          id: 'brandIds',
          name: intl.formatMessage({ id: 'page.home.filters.type.brand', defaultMessage: 'Brands' }),
          values: brands.map(({ id: value, name }) => ({
            value,
            name,
            className: 'default',
          })),
        },
      ];
    }
    return filters;
  }
);

export const canShowChatStatistics = createSelector(
  getDealersBySelectedGroupId,
  getSelectedDealerId,
  getSelectedDealerExtensions,
  (dealers, dealerId, extensions) => (
    dealerId
      ? extensions.includes(CHAT)
      : dealers.some(({ extensions: dealerExtensions }) => dealerExtensions?.includes(CHAT) ?? false)
  )
);

const getQuestionSelfService = createSelector(getUI, ({ questionSelfService }) => questionSelfService);
const getSelectQuestionSelfService = createSelector(getQuestionSelfService, ({ select }) => select);
export const getQuestionSelfServiceIds = createSelector(getSelectQuestionSelfService, ({ ids }) => ids);
export const getQuestionSelfServicePage = createSelector(getSelectQuestionSelfService, ({ page }) => page);
export const getQuestionSelfServiceHasMore = createSelector(getSelectQuestionSelfService, ({ hasMore }) => hasMore);
export const getQuestionSelfServiceLoading = createSelector(getSelectQuestionSelfService, ({ loading }) => loading);

export const getDetailsStatsSelfServiceFilters = createSelector(getUISelfServiceDetails, ({ filters }) => filters);

export const getDetailsStatsSelfServiceGroupBy = createSelector(
  getPeriod,
  getDetailsStatsSelfServiceFilters,
  (period, { groupBy }) => getEffectiveGroupBy(period, groupBy)
);

export const isHomeCheckIn = createSelector(
  getDetailsStatsSelfServiceFilters,
  ({ type, origin }) => type === SELF_SERVICE_TYPE.CHECK_IN && SELF_SERVICE_STATS_ORIGIN.HOME === origin
);
export const isKioskOrOnSiteCheckIn = createSelector(
  getDetailsStatsSelfServiceFilters,
  ({ type, origin }) => type === SELF_SERVICE_TYPE.CHECK_IN && SELF_SERVICE_STATS_ORIGIN.ON_SITE_OR_KIOSK === origin
);
